// Analytics Tracking Utility for TechUnity-OS
// Simplified version to avoid import path issues

interface PageViewData {
  page: string;
  sessionId: string;
  userId?: string;
  userAgent?: string;
  referrer?: string;
  country?: string;
  city?: string;
}

interface AnalyticsConfig {
  apiBaseUrl: string;
  enabled: boolean;
  debug: boolean;
}

class AnalyticsTracker {
  private config: AnalyticsConfig;
  private sessionId: string;
  private pageStartTime: number = 0;
  private currentPage: string = '';

  constructor(config: AnalyticsConfig) {
    this.config = config;
    this.sessionId = this.generateSessionId();

    if (this.config.enabled && typeof window !== 'undefined') {
      this.initializeTracking();
    }
  }

  private generateSessionId(): string {
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  private initializeTracking(): void {
    // Track initial page load
    this.trackPageView(this.getCurrentPath());

    // Track page changes for SPAs
    this.setupSPATracking();

    // Track page visibility changes
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        this.trackPageDuration();
      } else {
        this.pageStartTime = Date.now();
      }
    });

    // Track page unload
    window.addEventListener('beforeunload', () => {
      this.trackPageDuration();
    });
  }

  private getCurrentPath(): string {
    // Support HashRouter by using the hash when present
    if (typeof window !== 'undefined' && window.location.hash) {
      const hashPath = window.location.hash.replace(/^#/, '');
      return hashPath || '/';
    }
    return typeof window !== 'undefined' ? window.location.pathname : '/';
  }

  private setupSPATracking(): void {
    // Override pushState and replaceState for SPA navigation
    const originalPushState = history.pushState;
    const originalReplaceState = history.replaceState;

    history.pushState = (...args) => {
      this.trackPageDuration();
      originalPushState.apply(history, args);
      setTimeout(() => this.trackPageView(this.getCurrentPath()), 0);
    };

    history.replaceState = (...args) => {
      this.trackPageDuration();
      originalReplaceState.apply(history, args);
      setTimeout(() => this.trackPageView(this.getCurrentPath()), 0);
    };

    // Handle back/forward navigation
    window.addEventListener('popstate', () => {
      this.trackPageDuration();
      setTimeout(() => this.trackPageView(this.getCurrentPath()), 0);
    });

    // Handle hash-based navigation (HashRouter)
    window.addEventListener('hashchange', () => {
      this.trackPageDuration();
      setTimeout(() => this.trackPageView(this.getCurrentPath()), 0);
    });
  }

  private async getLocationData(): Promise<{ country?: string; city?: string }> {
    try {
      // Use backend proxy
      const response = await fetch(`${this.config.apiBaseUrl}/system/location`);
      if (response.ok) {
        const data = await response.json();
        return {
          country: data.country_name,
          city: data.city
        };
      }
    } catch (error) {
      if (this.config.debug) {
        console.warn('Failed to get location data:', error);
      }
    }
    return {};
  }

  private trackPageDuration(): void {
    if (this.pageStartTime > 0 && this.currentPage) {
      const duration = Math.floor((Date.now() - this.pageStartTime) / 1000);
      if (duration > 0) {
        this.sendAnalytics({
          page: this.currentPage,
          sessionId: this.sessionId,
          duration
        });
      }
    }
  }

  public async trackPageView(page: string): Promise<void> {
    if (!this.config.enabled) return;

    // Track duration of previous page
    this.trackPageDuration();

    this.currentPage = page;
    this.pageStartTime = Date.now();

    const locationData = await this.getLocationData();

    const pageViewData: PageViewData = {
      page,
      sessionId: this.sessionId,
      userAgent: navigator.userAgent,
      referrer: document.referrer,
      ...locationData
    };

    // Get user ID if available (from localStorage or other auth system)
    const userStr = localStorage.getItem('user') || localStorage.getItem('adminUser');
    if (userStr) {
      try {
        const user = JSON.parse(userStr);
        pageViewData.userId = user.id || user._id;
      } catch (e) {
        // Ignore parsing errors
      }
    }

    this.sendAnalytics(pageViewData);
  }

  private async sendAnalytics(data: any): Promise<void> {
    if (!this.config.enabled) return;

    try {
      await fetch(`${this.config.apiBaseUrl}/metrics/pageview`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });

      if (this.config.debug) {
        console.log('Analytics tracked:', data);
      }
    } catch (error) {
      if (this.config.debug) {
        console.error('Failed to send analytics:', error);
      }
    }
  }

  public trackCustomEvent(eventName: string, eventData: any = {}): void {
    if (!this.config.enabled) return;

    const customEventData = {
      event: eventName,
      page: this.currentPage,
      sessionId: this.sessionId,
      timestamp: new Date().toISOString(),
      ...eventData
    };

    if (this.config.debug) {
      console.log('Custom event tracked:', customEventData);
    }
  }

  public setUserId(userId: string): void {
    this.trackCustomEvent('user_identified', { userId });
  }
}

// Factory function to create analytics instance
export function createAnalyticsTracker(config: Partial<AnalyticsConfig> = {}): AnalyticsTracker {
  const defaultConfig: AnalyticsConfig = {
    apiBaseUrl: import.meta.env.VITE_ANALYTICS_ENDPOINT || 'https://techunity-ghbh.onrender.com/api',
    enabled: import.meta.env.VITE_ENABLE_ANALYTICS === 'true',
    debug: import.meta.env.DEV === true
  };

  return new AnalyticsTracker({ ...defaultConfig, ...config });
}

export default AnalyticsTracker;
